import React, { useState, useEffect, useCallback } from "react";
import api from "../api/homeapi";
import { StaffFrame, DataTable, PageHead, SearchBox, ClassChip, StatusPill, fmtPrice } from "./DashboardShared";
import { downloadTicketsPdf } from "../utils/ticketPdf";
import { formatDateFR } from "../utils/format";
import { useNotify, errorMessage } from "../components/Notify";
import { DownloadIcon, RefreshIcon } from "../components/Icons";

const PER_PAGE = 15;

export default function AdminBookings() {
  const notify = useNotify();
  const [bookings, setBookings] = useState([]);
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [status,   setStatus]   = useState("ALL");
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/bookings?page=${p}`);
      const rows = res.data?.data ?? res.data;
      setBookings(Array.isArray(rows) ? rows : []);
      setTotal(res.data?.total || 0);
      setPage(p);
    } catch (e) {
      notify.error(errorMessage(e, "Impossible de charger les réservations."));
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const filtered = bookings.filter(b =>
    (status === "ALL" || b.status === status) &&
    (!q || b.reference?.toLowerCase().includes(q) || b.fullName?.toLowerCase().includes(q) || b.route?.toLowerCase().includes(q)));

  const downloadOne = async (b) => {
    setDownloadingId(b.id);
    try {
      await downloadTicketsPdf([{
        reference: b.reference, passenger: b.fullName, seat: b.seatNumber,
        depart: (b.route || "").split(" → ")[0], arrivee: (b.route || "").split(" → ")[1],
        date: b.date, heure: b.departureTime, train: b.trainNumber, classe: b.travelClass,
        prix: b.amount, tripType: "ALLER", qrPayload: b.qrPayload || null,
      }], `billet-setrag-${b.reference || b.id}.pdf`);
      notify.success(`Billet ${b.reference} téléchargé.`);
    } catch (e) {
      notify.error(errorMessage(e, "La génération du PDF a échoué."), { title: "Téléchargement impossible" });
    } finally { setDownloadingId(null); }
  };

  const pages = Math.ceil(total / PER_PAGE);

  return (
    <StaffFrame>
      <div className="page">
        <PageHead title="Réservations" sub={`Toutes les réservations de billets SETRAG${total ? ` · ${total} au total` : ""}`}>
          <SearchBox value={search} onChange={setSearch} placeholder="Réf., passager, trajet…" />
          <button className="btn btn-ghost" onClick={() => load(page)} title="Actualiser"><RefreshIcon size={14}/> Actualiser</button>
        </PageHead>

        <div className="filter-row">
          {[["ALL", "Toutes"], ["CONFIRMED", "Confirmées"], ["PENDING", "En attente"], ["CANCELLED", "Annulées"]].map(([v, l]) => (
            <button key={v} className={`filter-btn${status === v ? " active" : ""}`} onClick={() => setStatus(v)}>{l}</button>
          ))}
        </div>

        {loading ? (
          <div className="loading-block"><div className="spinner"/>Chargement des réservations…</div>
        ) : (
          <>
            <DataTable stack="md" rows={filtered} empty={search ? "Aucune réservation ne correspond à votre recherche." : "Aucune réservation trouvée."} columns={[
              { key: "p", label: "Passager", main: true, render: b => (
                <div><div style={{ fontWeight: 700 }}>{b.fullName}</div><span className="ref-chip" style={{ marginTop: 4, display: "inline-block" }}>{b.reference}</span></div>) },
              { key: "route", label: "Trajet", className: "td-wrap", render: b => <span>{b.route} <span className="muted small">· {b.trainNumber}</span></span> },
              { key: "class", label: "Classe", render: b => <ClassChip value={b.travelClass}/> },
              { key: "date", label: "Date", nowrap: true, render: b => <span className="mono">{formatDateFR(b.date)}</span> },
              { key: "amount", label: "Montant", render: b => <span className="money">{fmtPrice(b.amount)}</span> },
              { key: "status", label: "Statut", render: b => <StatusPill value={b.status}/> },
              { key: "ticket", label: "Billet", actions: true, render: b => b.status === "CONFIRMED"
                  ? <button className="btn btn-primary btn-sm" onClick={() => downloadOne(b)} disabled={downloadingId === b.id}>
                      <DownloadIcon size={13}/> {downloadingId === b.id ? "Génération…" : "Télécharger"}
                    </button>
                  : <span className="muted small">Billet indisponible</span> },
            ]}/>

            {pages > 1 && (
              <div className="pager">
                <button className="pager-btn" onClick={() => load(page - 1)} disabled={page === 1} aria-label="Page précédente">←</button>
                {[...Array(pages)].map((_, i) => (
                  <button key={i} className={`pager-btn${page === i + 1 ? " active" : ""}`} onClick={() => load(i + 1)}>{i + 1}</button>
                ))}
                <button className="pager-btn" onClick={() => load(page + 1)} disabled={page === pages} aria-label="Page suivante">→</button>
              </div>
            )}
          </>
        )}
      </div>
    </StaffFrame>
  );
}
